import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ItemSourceGuesserItemsModel from 'models/ItemSourceGuesserItemsModel';
import { ItemSourceGuesserModel } from 'models/ItemSourceGuesserModel';
import { useForceRender } from 'hooks/useForceRender';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import { AsyncTaskStatus, ItemSourceGuesserItemStatus as ItemStatus } from 'constants/itemSourceGuesserStatus';
import { asyncTaskStatusRenderer } from '../../../CMFW/grid/cellRenderers/asyncTaskStatus';
import { IFields } from 'CMFW/grid/fields';
import { compareAiConfidence } from 'CMFW/grid/comparators/compareAiConfidence';
import ItemSourceGuesserSummary from './ItemSourceGuesserSummary';
import ItemSourceGuesserBulkActions from './ItemSourceGuesserBulkActions';
import { itemSourceGuesserItemStatusRenderer } from '../../../CMFW/grid/cellRenderers/itemSourceGuesserItemStatus';
import PrioritySelector from 'components/PrioritySelector';
import { StyledTargetSourceRenderer } from '../../../CMFW/grid/cellRenderers/StyledTargetSourceRenderer';

let currentFilter: { type: 'status' | 'decision'; value: string } | null = null;

const ItemSourceGuesserDetails: React.FC = () => {
  const model = useContext(ModuleContext) as ItemSourceGuesserModel;
  const jobId = model.itemId;

  const forceRender = useForceRender();
  const [activeFilter, setActiveFilter] = useState<{ type: 'status' | 'decision'; value: string } | null>(null);
  const [jobName, setJobName] = useState<string>('');
  const [jobPriority, setJobPriority] = useState<number | undefined>(undefined);
  currentFilter = activeFilter;

  const itemsModel = useMemo(() => new ItemSourceGuesserItemsModel({ render: forceRender }), [forceRender]);
  useEffect(() => {
    if (jobId > 0) {
      itemsModel.setJobId(jobId);
      setActiveFilter(null);
      // Load job details to get the name and priority
      model.loadItemData(jobId).then((data) => {
        if (data) {
          if (data.name) {
            setJobName(data.name);
          }
          if (data.priority !== undefined) {
            setJobPriority(data.priority);
          }
        }
      });
    }
  }, [jobId, itemsModel, model]);

  useEffect(() => {
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [activeFilter, itemsModel]);

  const handleChangePriority = async (newPriority: number) => {
    await model.changePriority(jobId, newPriority);
    setJobPriority(newPriority);
  };

  const isFilterActive = () => currentFilter !== null;
  const isRowVisible = (node: any) => {
    if (!currentFilter) return true;
    const data = node.data;
    if (!data) return true;
    switch (currentFilter.type) {
      case 'status':
        if (currentFilter.value === 'processing') return data.status === ItemStatus.PENDING;
        if (currentFilter.value === 'review') return data.status === ItemStatus.NEEDS_USER_REVIEW;
        if (currentFilter.value === 'error') {
          return (
            data.async_task_status === AsyncTaskStatus.FAILED ||
            data.async_task_status === AsyncTaskStatus.DEAD_LETTER ||
            data.ai_result?.decision === 'error'
          );
        }
        return true;
      case 'decision':
        // Always filter by AI decision, not user's selection
        return data.ai_result?.decision === currentFilter.value;
      default:
        return true;
    }
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.itemSourceGuesserActions]: 'itemSourceGuesserActionRenderer',
      [IFields.itemSourceGuesserAiStatus]: asyncTaskStatusRenderer,
      [IFields.itemSourceGuesserAiResult]: 'itemSourceGuesserAiResultRenderer',
      [IFields.itemSourceGuesserStatus]: 'itemSourceGuesserItemStatusRenderer',
      [IFields.itemSourceGuesserItem]: StyledTargetSourceRenderer,
      [IFields.conceptValidatorItemStatus]: itemSourceGuesserItemStatusRenderer,
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
    }),
    [],
  );

  const columnSorters = useMemo(() => ({ [IFields.itemSourceGuesserAiResult]: compareAiConfidence }), []);

  const gridOptions = useMemo(
    () => ({
      getRowStyle: (params: any) => {
        if (params.data) {
          if (
            params.data.async_task_status === AsyncTaskStatus.FAILED ||
            params.data.async_task_status === AsyncTaskStatus.DEAD_LETTER
          ) {
            return { backgroundColor: '#ffebee' };
          }
          if (typeof params.data.status === 'number') {
            if (params.data.status === ItemStatus.PENDING) return { backgroundColor: '#fffef7' };
            if (params.data.status === ItemStatus.NEEDS_USER_REVIEW) return { backgroundColor: '#fff8f0' };
          }
        }
        return undefined;
      },
    }),
    [],
  );

  // If job is not yet available, render nothing after establishing hooks
  if (!jobId || jobId === -1) {
    return null;
  }

  return (
    <Container>
      <ValidatorHeader
        id={jobId}
        name={jobName}
        idLabel="Job"
        bulkActions={
          <>
            <ItemSourceGuesserBulkActions itemsModel={itemsModel} model={model} onActionComplete={forceRender} />
            <PrioritySelector
              currentPriority={jobPriority ?? 1}
              onChangePriority={handleChangePriority}
              isProcessing={itemsModel.gridData?.some((item: any) => item.status === ItemStatus.PENDING) ?? false}
            />
          </>
        }
        itemsModel={itemsModel}
      />

      <ItemSourceGuesserSummary
        itemsModel={itemsModel}
        onFilterChange={setActiveFilter}
        activeFilter={activeFilter}
        onClearFilter={() => setActiveFilter(null)}
      />

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid
            cellRenderers={cellRenderers}
            gridOptions={gridOptions}
            isFilterActive={isFilterActive}
            isRowVisible={isRowVisible}
            columnSorters={columnSorters}
          />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={model}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default ItemSourceGuesserDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;
const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
